/**
 * ConnectionStatusBanner
 *
 * A non-intrusive, auto-hiding banner that tells the user what's happening
 * with the WebSocket connection. Shows:
 *
 *  • Reconnecting — attempt N of M, retrying in Xs
 *  • Offline — browser has no network
 *  • Connection lost — after all retries exhausted
 *  • Latency warning — when ping > threshold
 *  • Connected (briefly) — after recovering from a disconnect
 *
 * Automatically hides when the connection is healthy.
 */

import React, { useEffect, useState } from "react";
import { useWebSocket } from "../../contexts/WebSocketProvider";
import {
  SLOW_LATENCY_THRESHOLD_MS,
  type ConnectionState,
} from "../../services/socketService";
import "./ConnectionStatusBanner.css";

/**
 * Latency above this (ms) is "slow" — re-exported from socketService, the
 * single source of truth, for anywhere (including tests) that wants the
 * raw number for display. The gating decision itself uses the debounced
 * `isLatencyElevated` flag on ConnectionHealth, not a direct comparison
 * against this constant — see socketService's SLOW_LATENCY_CONSECUTIVE_SAMPLES.
 */
const HIGH_LATENCY_THRESHOLD = SLOW_LATENCY_THRESHOLD_MS;

/** How long to show the "reconnected" success banner (ms) */
const RECONNECTED_DISPLAY_MS = 3000;

interface BannerConfig {
  message: string;
  detail?: string;
  variant: "warning" | "error" | "info" | "success";
  showRetry?: boolean;
}

function getBannerConfig(
  state: ConnectionState,
  reconnectAttempt: number,
  maxAttempts: number,
  reconnectInSeconds: number | null,
  isNetworkOnline: boolean,
  latencyMs: number | null,
  isLatencyElevated: boolean,
): BannerConfig | null {
  // Network offline takes priority
  if (!isNetworkOnline) {
    return {
      message: "You are offline",
      detail:
        "Check your internet connection. We'll reconnect automatically when you're back online.",
      variant: "error",
    };
  }

  switch (state) {
    case "reconnecting":
      return {
        message: "Connection lost — reconnecting…",
        detail:
          reconnectInSeconds != null
            ? `Attempt ${reconnectAttempt} of ${maxAttempts} · retrying in ${reconnectInSeconds}s`
            : `Attempt ${reconnectAttempt} of ${maxAttempts}`,
        variant: "warning",
      };

    case "connecting":
      return {
        message: "Connecting…",
        variant: "info",
      };

    case "authenticating":
      return {
        message: "Authenticating…",
        variant: "info",
      };

    case "failed":
      return {
        message: "Unable to connect to the server",
        detail:
          "All reconnection attempts failed. Please check your connection and try again.",
        variant: "error",
        showRetry: true,
      };

    case "authenticated":
      // Debounced: several consecutive slow probes, not just this one
      // sample — a single blip on a mobile connection shouldn't alarm
      // the user. latencyMs is still the real, current reading for the
      // detail text once the debounced signal does trip.
      if (isLatencyElevated && latencyMs != null) {
        return {
          message: "Slow connection detected",
          detail: `Latency: ${latencyMs}ms — some actions may feel delayed.`,
          variant: "warning",
        };
      }
      return null; // healthy — hide banner

    case "disconnected":
    default:
      return null;
  }
}

export const ConnectionStatusBanner: React.FC = () => {
  const { health, reconnect } = useWebSocket();
  const [showReconnected, setShowReconnected] = useState(false);
  const [prevState, setPrevState] = useState<ConnectionState>(health.state);

  // Detect recovery: was reconnecting/failed → now authenticated
  useEffect(() => {
    const wasDown =
      prevState === "reconnecting" ||
      prevState === "failed" ||
      prevState === "connecting";
    const isUp = health.state === "authenticated";

    if (wasDown && isUp) {
      setShowReconnected(true);
      const timer = setTimeout(
        () => setShowReconnected(false),
        RECONNECTED_DISPLAY_MS,
      );
      return () => clearTimeout(timer);
    }

    setPrevState(health.state);
  }, [health.state, prevState]);

  // While showing "reconnected" success banner
  if (showReconnected) {
    return (
      <div
        className="connection-banner connection-banner--success"
        role="status"
      >
        <span className="connection-banner__icon">✓</span>
        <span className="connection-banner__message">Connection restored</span>
        {health.latencyMs != null && (
          <span className="connection-banner__latency">
            {health.latencyMs}ms
          </span>
        )}
      </div>
    );
  }

  const config = getBannerConfig(
    health.state,
    health.reconnectAttempt,
    health.maxReconnectAttempts,
    health.reconnectInSeconds,
    health.isNetworkOnline,
    health.latencyMs,
    health.isLatencyElevated,
  );

  if (!config) return null;

  return (
    <div
      className={`connection-banner connection-banner--${config.variant}`}
      role="alert"
      aria-live="polite"
    >
      <div className="connection-banner__content">
        <span className="connection-banner__message">{config.message}</span>
        {config.detail && (
          <span className="connection-banner__detail">{config.detail}</span>
        )}
      </div>
      {config.showRetry && (
        <button
          className="connection-banner__retry"
          onClick={reconnect}
          type="button"
        >
          Retry Now
        </button>
      )}
    </div>
  );
};

export default ConnectionStatusBanner;

// Export for testing
export { getBannerConfig, HIGH_LATENCY_THRESHOLD };
